import { Component } from '@angular/core';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { CommonModule } from '@angular/common';
import { FormControl } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';

/** デバイスのViewModel */
export type DeviceViewModel = {
  /** シリアル番号 */
  serialNo: string;
  /** デバイス種類ID */
  deviceTypeId: string;
  /** テナントID */
  tenantId: string;
  /**LPN ID */
  lpnId: string;
  /** 契約情報 5分契約 */
  contractType: string;

  /** 契約開始日 */
  contractStartedAt: Date;

  /** 契約開始日 */
  contractExpiredAt: Date;
};

/** デバイスのViewModel */
export type DeviceViewModelBulkAdd = {
  /** シリアル番号 */
  serialNo: string;
  /** デバイス種類ID */
  deviceTypeId: string;
  /** デバイス種類表示名（CSVの解決規則から生成） */
  deviceTypeDisplayName?: string;
  /** テナントID */
  tenantId: string;
  /**LPN ID */
  lpnId: string;
  /** 契約情報 5分契約 */
  contractType: string;

  /** 契約開始日 */
  contractStartedAt: Date | string;

  /** 契約開始日 */
  contractExpiredAt: Date | string;
};

/** ヘッダーのマッピング */
const headerMapping: { [key: string]: string } = {
  購買組織: 'purchasingOrganization',
  プロダクトコード: 'productCode',
  FS担当者: 'fsResponsiblePerson',
  更新通知宛先: 'updateNotificationRecipient',
  受注先: 'orderDestination',
  品目コード: 'itemCode',
  数量: 'quantity',
  保守対象品目コード: 'deviceTypeId',
  保守対象品目テキスト: 'tenantId',
  機器SN: 'serialNo',
  契約開始日: 'contractStartedAt',
  契約終了日: 'contractExpiredAt',
  受注伝票番号: 'orderSlipNumber',
  客先注文番号: 'contractType',
  購買発注番号: 'purchasingOrderNumber',
  ライセンスID: 'lpnId',
  備考: 'remarks',
};

@Component({
  selector: 'app-device-bulk-add-dialog',
  standalone: true,
  templateUrl: './device-bulk-add-dialog.component.html',
  styleUrl: './device-bulk-add-dialog.component.scss',
  imports: [
    CommonModule,
    MatDialogModule,
    MatIconModule,
    MatButtonModule,
    MatFormFieldModule,
  ],
})
export class DeviceBulkAddDialogComponent {
  /** インポートしたファイル名 */
  public fileName = 'ファイルが選択されていません';

  /** インポートしたデバイス情報一覧 */
  public deviceList: DeviceViewModel[] = [];

  /** ファイルフォームコントロール */
  public fileControl: FormControl<string | null> = new FormControl('');

  /** エラーメッセージ */
  public fileError: string | null = null;

  constructor(private dialogRef: MatDialogRef<DeviceBulkAddDialogComponent>) {}

  public onFileSelected() {
    const inputNode: HTMLInputElement | null = document.querySelector('#file');
    this.deviceList = [];
    this.fileName = 'ファイルが選択されていません';
    this.fileError = null;

    if (typeof FileReader !== 'undefined') {
      const reader = new FileReader();

      reader.onload = () => {
        const csvText = reader.result as string;
        if (csvText !== null) {
          this.deviceList = this.parseCSV(csvText);
        }
      };

      if (inputNode?.files?.length) {
        const file = inputNode.files[0];
        const fileName = file.name;
        const fileExtension = fileName.split('.').pop()?.toLowerCase();

        if (fileExtension === 'csv') {
          this.fileName = fileName;
          this.fileControl.setErrors(null); // エラーをクリア
          reader.readAsText(file, 'SJIS');
        } else {
          this.fileError = 'CSVファイルのみを選択してください。';
          this.fileControl.setErrors({
            incorrectFileType: true,
          });
          inputNode.value = ''; // 選択をクリア
        }
      }
    }
  }

  private parseCSV(csvText: string): DeviceViewModel[] {
    const lines = csvText.split('\n');
    const headers = lines[0].split(',').map((header) => header.trim());
    const mappedHeaders = headers.map(
      (header) => headerMapping[header] || header,
    );
    const columnCount = headers.length;

    const modifiedLines = [];
    let currentLine = '';

    // ダブルクオートで囲まれたフィールドが途中で改行されている場合、行を結合する
    for (const line of lines.slice(1)) {
      if ((currentLine.match(/"/g) || []).length % 2 === 0) {
        // 現在の行が完全な行であれば、処理を行う
        modifiedLines.push(currentLine);
        currentLine = line;
      } else {
        // 現在の行が不完全であれば、次の行と結合する
        currentLine += '\n' + line;
      }
    }
    // 最後の行を追加
    if (currentLine) {
      modifiedLines.push(currentLine);
    }

    // 空行をカラム数に対応したカンマに変換し、空行を除外
    const processedLines = modifiedLines.map((modifiedLine) =>
      modifiedLine.trim() === '' ? ','.repeat(columnCount - 1) : modifiedLine,
    );

    return processedLines.map((line) => {
      const values: string[] = [];
      let currentValue = '';
      let insideQuotes = false;

      // ダブルクォートで囲まれている場合はカンマを含む場合も一つのフィールドとみなす
      for (const char of line) {
        if (char === '"') {
          insideQuotes = !insideQuotes;
        } else if (char === ',' && !insideQuotes) {
          values.push(currentValue.trim());
          currentValue = '';
        } else {
          currentValue += char;
        }
      }
      // Push the last value
      values.push(currentValue.trim());

      const obj: DeviceViewModel = {
        serialNo: '',
        deviceTypeId: '',
        tenantId: '',
        lpnId: '',
        contractType: '',
        contractStartedAt: new Date(),
        contractExpiredAt: new Date(),
      };

      mappedHeaders.forEach((header, index) => {
        if (header in obj) {
          (obj as Record<string, unknown>)[header] = values[index];
        }
      });

      return obj;
    });
  }

  public bulkAdd(): void {
    this.dialogRef.close(this.deviceList);
  }
}
